<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, nextTick } from "vue";
import { type ControlSize, control } from "@/design/control";
import Field from "./Field.vue";

/**
 * Uma escolha, com um `<select>` NATIVO por dentro.
 *
 * Nativo porque no telefone o seletor do sistema é melhor que qualquer lista que
 * a gente desenhe, e porque uma lista desenhada custa foco preso, teclado e
 * colisão de borda de tela — coisas que a Fase 2 compra de uma vez, para todo
 * mundo, e não aqui.
 *
 * O que o nativo não dá é a seta: `appearance-none` apaga o chrome do sistema
 * (que ignora borda e raio) e a seta volta desenhada. O tema dos controles do
 * sistema já está resolvido por `color-scheme` em `tokens.css`.
 */
defineOptions({ inheritAttrs: false });

const props = defineProps<{
	label?: string;
	modelValue: string;
	/** As opções, quando são dados. `<option>` à mão continua valendo pelo slot. */
	options?: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
	/** Rótulo da opção vazia. Sem ele, não existe opção vazia. */
	placeholder?: string;
	size?: ControlSize;
	hint?: string;
	error?: string;
	/** Inválido sem mensagem própria — o erro está sendo desenhado por fora. */
	invalid?: boolean;
	describedBy?: string;
	id?: string;
	required?: boolean;
	disabled?: boolean;
	class?: string;
}>();
const emit = defineEmits<{ "update:modelValue": [string] }>();

/**
 * Controlado de verdade: quem usa pode recusar a escolha (perguntar antes, falhar),
 * e o `<select>` nativo não volta sozinho quando o `modelValue` não muda.
 */
function pick(event: Event): void {
	const select = event.target as HTMLSelectElement;
	emit("update:modelValue", select.value);
	void nextTick(() => {
		select.value = props.modelValue;
	});
}

const classes = computed(() =>
	twMerge(control({ size: props.size }), "appearance-none pr-8", props.class),
);
</script>

<template>
	<Field
		v-slot="{ id: controlId, describedBy: describes, invalid: wrong }"
		:label="label"
		:hint="hint"
		:error="error"
		:required="required"
		:invalid="invalid"
		:described-by="describedBy"
		:id="id"
	>
		<div class="relative">
			<select
				:id="controlId"
				:value="modelValue"
				:required="required"
				:disabled="disabled"
				:aria-invalid="wrong"
				:aria-describedby="describes"
				:class="classes"
				v-bind="$attrs"
				@change="pick"
			>
				<option v-if="placeholder !== undefined" value="">{{ placeholder }}</option>
				<option
					v-for="option in options ?? []"
					:key="option.value"
					:value="option.value"
					:disabled="option.disabled"
				>
					{{ option.label }}
				</option>
				<slot />
			</select>
			<svg
				class="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-subtle"
				viewBox="0 0 16 16"
				fill="none"
				stroke="currentColor"
				stroke-width="1.5"
				aria-hidden="true"
			>
				<path d="m4 6 4 4 4-4" stroke-linecap="round" stroke-linejoin="round" />
			</svg>
		</div>
	</Field>
</template>
